const validationReporter = require('../../../../helpers/validationReporter').validationReporter;
const Blockly = require('blockly');
const dataAccessMethods = require('../../../../dataAccessMethods');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');
const addForActionDropdown = require('../../../../helpers/addForActionDropdown');
const createListener = require('../../../../helpers/createListener');
const mdcConfig = require('@sstdev/lib_metadata-config');
const { config } = mdcConfig;

const TOOLTIP = 'Generic toggle for use outside forms for more global actions';
/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    let workspace = blocklyBlock.workspace;
    let blockId = blocklyBlock.id;
    const { BLOCK_CHANGE, BLOCK_CREATE, FINISHED_LOADING } = Blockly.Events;
    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged vertically.
    //Meaning each (dummy)input starts a new line
    blocklyBlock.setInputsInline(false);
    line1OfInputs.appendField('  Property Name').appendField(new Blockly.FieldTextInput(), 'propertyName');

    const defaultValueSelectors = dataAccessMethods.getDefaultValueSelectors();
    line1OfInputs
        .appendField('  Negative (false) Option:')
        .appendField(new Blockly.FieldTextInput(''), 'toggleOption1')
        .appendField('  Positive (true) Option:')
        .appendField(new Blockly.FieldTextInput('true'), 'toggleOption2')
        .appendField('  Default Value:')
        .appendField(new Blockly.FieldDropdown(defaultValueSelectors), 'defaultValueSelector')
        .appendField(new Blockly.FieldTextInput('false'), 'defaultValue');

    const scopeDropdown = new Blockly.FieldDropdown(config.dataRetention.scopeOptions, null);
    scopeDropdown.setValue('global');
    const durationDropdown = new Blockly.FieldDropdown(config.dataRetention.durationOptions, newValue => {
        if (newValue === 'session') {
            return 'pageLoad';
        }
        return newValue;
    });
    const visibilityDropdown = new Blockly.FieldDropdown(config.dataRetention.visibilityOptions);
    blocklyBlock
        .appendDummyInput()
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Selection Scope:')
        .appendField(scopeDropdown, 'scope')
        .appendField('  Selection Duration:')
        .appendField(durationDropdown, 'duration')
        .appendField('  Selection Visibility:')
        .appendField(visibilityDropdown, 'visibility');

    // Start with all fields so Blockly can deserialize saved values, then apply
    // the same conditional fields in the editor and headless authoring workers.
    addNsRelDropdowns(blocklyBlock);
    createListener(workspace, blocklyBlock, event => {
        if (
            event.type === FINISHED_LOADING ||
            (event.blockId === blockId && [BLOCK_CHANGE, BLOCK_CREATE].includes(event.type))
        ) {
            const block = workspace.getBlockById(blockId);
            if (block) syncFields(block);
        }
    });
}

function syncWorkspace(workspace) {
    workspace.getBlocksByType('actionElement_Toggle', false).forEach(syncFields);
}

function syncFields(block) {
    const topInput = block.getInput('topInput');
    if (block.getFieldValue('defaultValueSelector') !== 'static') {
        if (block.getField('defaultValue')) topInput.removeField('defaultValue');
    } else if (!block.getField('defaultValue')) {
        topInput.appendField(new Blockly.FieldTextInput('false'), 'defaultValue');
    }
    if (block.getFieldValue('visibility') === 'perBrowser') {
        if (block.getInput('namespaceRelation')) block.removeInput('namespaceRelation');
    } else if (!block.getInput('namespaceRelation')) {
        addNsRelDropdowns(block);
    }
}

function addNsRelDropdowns(block) {
    const nsRelInput = block
        .appendDummyInput('namespaceRelation')
        .setAlign(Blockly.ALIGN_RIGHT)
        .appendField('  Namespace:')
        .appendField(
            new Blockly.FieldDropdown(() => {
                const ns = dataAccessMethods.getNamespaces();
                ns.unshift(['Nothing', '']);
                return ns;
            }),
            'namespace'
        )
        .appendField('  Relation:')
        .appendField(
            new Blockly.FieldDropdown(() => dataAccessMethods.getRelations(block.workspace, block.id)),
            'relation'
        );
    addForActionDropdown(block, nsRelInput);
    nsRelInput.appendField('  (optional) Subtype:').appendField(new Blockly.FieldTextInput(), 'subtype');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);
    // These are most common, but we overwrite/clear for specific HNodes

    //nsAndRel
    addCodeProperties(['namespace', 'relation', 'forAction', 'subtype']);

    //action
    addCodeProperties(['propertyName', 'defaultValueSelector']);
    const opt1 = blocklyBlock.getFieldValue('toggleOption1');
    const opt2 = blocklyBlock.getFieldValue('toggleOption2');
    let defaultValue = blocklyBlock.getFieldValue('defaultValue');
    switch (defaultValue) {
        case undefined:
        case null:
        case 'false': // default value for block's field control (must be string)
        case opt1:
            defaultValue = false;
            break;
        case 'true':
        case opt2:
            defaultValue = true;
            break;
        default:
            validationReporter(
                `The default value of ${defaultValue} is not valid.  Use true, false or nothing.`,
                blocklyBlock.id
            );
    }
    addCodeProperties([
        { key: 'toggleValues', handler: () => [opt1, opt2] },
        { key: 'defaultValue', handler: () => defaultValue }
    ]);
    code.retention = {
        duration: blocklyBlock.getFieldValue('duration'),
        scope: blocklyBlock.getFieldValue('scope'),
        visibility: blocklyBlock.getFieldValue('visibility'),
        // Make it easier to pass retention options for consumers of hNode.retention
        // by adding the id for them.
        id: code.id
    };
    return code;
}
module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, syncFields, syncWorkspace };
